import { Report } from './reports.model';
import { User } from './users.model';
import { UserAssignees } from './assignees.model';
import { Attachment } from './files.model';
import { ReplyAttached } from './reply.model';
import { MetaData } from './shared.model';
import { Watchers } from './watchers.model';
import { CustomField } from './custom-fields.model';
import { UntypedFormGroup } from '@angular/forms';

export interface UnifierSyncOutResults {
  status: string;
  status_code?: string;
  status_description?: string;
}

export interface TopUserData {
  pending: ObservationByUserType[];
  in_progress: ObservationByUserType[];
  completed: ObservationByUserType[];
  closed: ObservationByUserType[];
  // Assignments whose status is outside the 4 core ones (Unifier-imported statuses such as
  // open/approved/rejected/reviewed/active/inactive/canceled). Optional because older API
  // deployments don't return it.
  other?: ObservationByUserType[];
}

export interface ObservationByUserType {
  count: number;
  user_id: number;
  user_name: string;
}

export interface ObservationType {
  id: string;
  name: string;
}

export interface ObservationDetails {
  autodesk_issue_id: string;
  unifier_observation_id?: string;
  unifier_sequence_number?: string;
  unifier_total_record_revisions?: number;
  unifier_observation_link?: string;
  unifier_sync_out_results?: UnifierSyncOutResults | null;
  code_info: string;
  code_label: string;
  code: string;
  is_draft?: boolean;
  id: string;
  deleted_at?: string;
  // permissions: {
  //   text: string;
  //   value: boolean;
  // }[];
  project: {
    project_id: string;
    project_name_ar: string;
    project_name: string;
    project_name_en: string;
  };
  report: {
    report_id: string;
    report_name: string;
    report_is_deleted: boolean;
  };
  floor: {
    floor_id: string;
    floor_name: string;
    floor_image: string;
  };
  point: {
    point_id: string;
    point_name: string;
    point_x: string;
    point_y: string;
  };
  screen_shot: string;
  title: string;
  description: string;
  type: string;
  type_label: string;
  status: string;
  is_creator: boolean;
  privacy: string;
  unique_id: string;
  significance: string;
  significance_label: string;
  location_x: number;
  location_y: number;
  observation_flag: string;
  configurable_fields: { attribute: string; visible: boolean }[];
  created_at: string;
  due_date: string;
  // is_system: the creator is the hidden PPE AI author; show its name as sent (V2CORE-3362).
  creator: { uuid: string; name: string; status: string; is_system?: boolean };
  categories: {
    category_id: string;
    category_name_ar: string;
    category_name_en: string;
    category_name_ru?: string;
  }[];
  sub_categories: {
    category_id: string;
    category_name_ar: string;
    category_name_en: string;
    category_name_ru?: string;
  }[];
  categories_str: string;
  user_assignees_str: string;
  user_assignees: UserAssignees[];
  hashtags?: string[];
  count_of_attached: number;
  attached_files: Attachment[];
  evidence_file: string;
  evidence_file_type: string;
  count_of_replies: number;
  replies: CommentReplies[];
  permissions: ObservationPermissions[] | OldObservationPermissions[];
  watchers: string;
  recommended_corrective_action: string;
  custom_fields: CustomField[];
  logs_button: {
    label: string;
    logs_count: number;
  };
  /** PPE auto-detection (V2CORE-2901): true for AI-created rows. HOME may send 0/1 or "0"/"1". */
  is_ai_generated?: boolean | number | string;
  ppe_detection_confidence?: number | null;
}
export interface OldObservationPermissions {
  text: string;
  value: boolean;
}

export enum ObservationPermissions {
  // General Observation Permissions
  OPEN_OBSERVATIONS = 'OPEN_OBSERVATIONS',
  DELETE_OBSERVATION = 'DELETE_OBSERVATION',
  FILTER_OBSERVATIONS = 'FILTER_OBSERVATIONS',
  VIEW_ALL_OBSERVATIONS = 'VIEW_ALL_OBSERVATIONS',
  OPEN_OBSERVATION_DETAILS = 'OPEN_OBSERVATION_DETAILS',
  AUTODESK_OBSERVATION = 'AUTODESK_OBSERVATION',
  // Observation Management Permissions
  EDIT_OBSERVATION = 'EDIT_OBSERVATION',
  CAN_CLOSE_OBSERVATION = 'CAN_CLOSE_OBSERVATION',
  EDIT_OBSERVATION_CODE = 'EDIT_OBSERVATION_CODE',
  CHANGE_OBSERVATION_STATUS = 'CHANGE_OBSERVATION_STATUS',
  CREATE_REPLY_ON_OBSERVATION = 'CREATE_REPLY_ON_OBSERVATION',
  UPLOAD_OBSERVATION_EVIDENCE = 'UPLOAD_OBSERVATION_EVIDENCE',
}

export interface CommentReplies {
  reply_id?: string;
  replied_by?: string;
  text_reply?: string;
  replied_at?: string;
  reply_attached?: ReplyAttached[];
  mentions?: Watchers[];
}

export interface ObservationDetailsResponse {
  observation_details: ObservationDetails;
}

export interface ObservationCounts {
  total: string;
  pending: string;
  in_progress: string;
  completed: string;
  closed: string;
  actioned?: string;
}

export interface ObservationCategory {
  id: string;
  category_name_ar: string;
  category_name_en: string;
  category_name_ru?: string;
  children?: ObservationCategory[];
}

// Reusable option model for observation categories (used in creation forms metadata)
export interface ObservationCategoryOption {
  label: string;
  value: string | number;
  children?: ObservationCategory[];
}

export interface ObservationForm extends UntypedFormGroup {
  metadata?: {
    categories: ObservationCategoryOption[];
  };
}

export interface ObservationStatus {
  name: string;
  value: string;
  children?: ObservationCategory[];
}

export interface ObservationStatus {
  name: string;
  value: string;
}

export interface ConfigurableField {
  attribute: string;
  visible: boolean;
}

export interface ConfigurableField {
  attribute: string;
  visible: boolean;
}

export interface ConfigurableField {
  attribute: string;
  visible: boolean;
}

export interface ObservationFilterResponse {
  code: Array<{ id: string; name: string; info: string }>;
  observation_on: Array<{ id: string; name: string }>;
  observation_type: Array<{ id: string; name: string; order?: number }>;
  status: Array<{ id: string; name: string }>;
  creators: Array<{ id: string; name: string }>;
  user_assignees: Array<{ id: string; name: string }>;
  observation_categories: Array<{ id: string; name: string }>;
  reports: Array<{ id: string; name: string }>;
  floors: Array<{ id: string; name: string }>;
  significance: Array<{ id: string; name: string }>;
  privacy: Array<{ id: string; name: string }>;
  projects: Array<{
    id: string;
    depth: number;
    parent_id: string;
    project_name: string;
    project_name_en: string;
  }>;
  configurable_fields?: ConfigurableField[];
}

/*REFACTORED OBSERVATION MODEL START*/
export class ObservationRefactor {
  id: string;
  project_id: string;
  project_name: string;
  project_name_en: string;
  projects: Project;
  unique_id: string;
  unifier_observation_id?: string;
  unifier_sequence_number?: string;
  unifier_total_record_revisions?: string;
  screen_shot: string;
  title: string;
  description: string | null;
  type: string;
  status: string;
  privacy: string;
  significance: string;
  significance_label: string;
  location_x: number;
  location_y: number;
  observation_flag: string;
  created_at: string;
  updated_at: string;
  creator: User;
  due_date: string | null;
  categories: Category[];
  user_assignees: UserAssignees[];
  hashtags?: string[];
  count_of_attached: number;
  report: Report;
  floor: Floor;
  point: Point;
  information?: any;
  is_draft?: boolean;
  type_label?: string;
  /** PPE auto-detection (V2CORE-2901): true for AI-created rows. HOME may send 0/1 or "0"/"1". */
  is_ai_generated?: boolean | number | string;
  ppe_detection_confidence?: number | null;
}

interface Floor {
  floor_id: string;
  floor_name: string;
  floor_image: string;
}

interface Point {
  point_id: string;
  point_name: string;
}

export class File {
  id: string;
  file_type: string;
  file_id: string;
  original_name: string;
  original_path: string;
  original_extension: string;
  deleted_at: string | null;
  created_at: string;
  updated_at: string;
  created_by: number;
  category_id: string | null;
}

export class Category {
  id: string;
  observation_id: string;
  observation_category_id: string;
  created_at: string | null;
  updated_at: string | null;
  category: CategoryDetails;
}

export class CategoryDetails {
  id: string;
  name_ar: string;
  name_en: string;
  deleted_at: string | null;
  created_at: string;
  updated_at: string;
}

export class Assignee {
  id: string;
  observation_id: string;
  user_id: number;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  user: User;
}

export class Project {
  id: string;
  parent_id: string | null;
  project_name: string;
  project_name_en: string;
  parent: any; // Replace "any" with the appropriate type if you have more information about the "parent" property.
}

export class ResponseObservationData {
  status: 'success' | 'error';
  message: string;
  error_code: string | null;
  meta_data: MetaData; /*Public Module - Shared */
  data: ObservationRefactor[];
  configurable_fields?: Array<{ attribute: string; visible: boolean }>;
}

export class ObservationParamsOptions {
  project_id: string;
  title?: string;
  report_id?: string;
  floor_id?: string;
  point_id?: string;
  from?: string;
  to?: string;
  privacy?: 'private' | 'public';
  type?: string | string[]; // Support both single type and array of types for filtering
  significance?: 'high' | 'medium' | 'low' | 'good_practice' | 'opportunity_for_improvement';
  status?: 'pending' | 'in_progress' | 'completed' | 'closed';
  created_by?: string;
  observation_flag?: 'observation_on_360' | 'observation_on_floor_plan' | 'observation_on_document';
  categories?: string[];
  hashtags?: string[];
  assignees?: string;
  building_id?: string;
  unique_id?: string;
  per_page?: number;
  page?: number;
  sort_by: string;
  sort_dir: string;
  over_due_only: '1' | '0';
  code?: 'none' | 'code_a' | 'code_b' | 'code_c' | 'code_r';
  logs_parent?: string;
  logs_parent_type?: 'MIR' | 'WIR';
}

export interface ObservationListRequestBody {
  project_id: string;
  status?: string;
  per_page?: number;
  page?: number;
  sort_by?: string;
  sort_dir?: string;
  type?: string | string[];
  [key: string]: unknown;
}

/*REFACTORED OBSERVATION MODEL END*/
